import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { getUserData } from '@/constants/global';
import { cancellationKeys } from '@/hooks/queries/cancellations/keys';
import { fetchCancellationPage, useActiveCancellations } from '@/hooks/queries/cancellations/use-cancellations';
import { acquireLiveElectionHub, type HubStatus } from '@/lib/live-election-hub';

import {
	cancellationAlerts,
	claimChime,
	type ICancellationAlert,
	type IReceiveInput,
	rowSignature,
} from '../services/cancellation-alerts';
import { type ICancellationCapabilities, type ICancellationChangedEvent, type ICancellationRow } from '../types';
import { type ICancellationScope } from './use-cancellation-scope';

export const POLL_VISIBLE_MS = 30_000;
export const POLL_HIDDEN_MS = 120_000;

/**
 * Whether the signed-in user made this change: they recorded the unit (Recorded) or changed
 * its state (Withdrawn / Reinstated). An admin who just pasted INEC's list must not be
 * "alerted" about the units they typed. Unknown ids are never "own".
 */
export const isOwnChange = (input: IReceiveInput, selfUserId: string | number | null | undefined): boolean => {
	const self = selfUserId === null || selfUserId === undefined ? '' : String(selfUserId).trim();
	if (!self || self === 'null') return false;
	const actor =
		input.changeKind === 'Recorded'
			? input.row.recordedByUserId
			: input.changeKind === 'Withdrawn' || input.changeKind === 'Reinstated'
				? input.row.stateChangedByUserId
				: null;
	return actor !== null && actor !== undefined && String(actor) === self;
};

/**
 * A row that left the active list could have been withdrawn by the agent OR reinstated by an
 * admin — the poll alone cannot tell, and the row it still holds is the OLD (already seen) one.
 * Ask the ledger for that unit in every state and return the fresh row; `null` when the lookup
 * fails or the row is (again) active, and the caller falls back to a silent "Gone".
 */
export const resolveGoneRow = async (
	electionId: number,
	partyId: number | null,
	row: ICancellationRow
): Promise<ICancellationRow | null> => {
	try {
		const params = new URLSearchParams({ pageNumber: '1', pageSize: '20', state: 'All', pollingUnitCode: row.pollingUnitCode });
		if (partyId !== null && partyId > 0) params.set('partyId', String(partyId));
		const page = await fetchCancellationPage(electionId, params);
		const fresh = page.rows.find((r) => r.id === row.id);
		if (fresh?.state === undefined || fresh.state === 'Active') return null;
		return fresh;
	} catch {
		return null;
	}
};

/**
 * Refresh what the Cancelled units page shows after a change arrived — the ledger pages, the
 * impact summary and per-unit impact — but NOT the active list the feed itself polls: that
 * query is where the change came from, and invalidating it would refetch → diff → invalidate.
 */
export const invalidateLedgerViews = (queryClient: QueryClient, electionId: number, partyKey: number) => {
	const prefix = cancellationKeys.scope(electionId, partyKey);
	void queryClient.invalidateQueries({
		predicate: (query) => {
			const key = query.queryKey;
			if (key.length <= prefix.length || !prefix.every((part, i) => key[i] === part)) return false;
			return key[prefix.length] === 'list' || key[prefix.length] === 'impact-summary' || key[prefix.length] === 'impact';
		},
	});
};

/**
 * What changed between two polls of the active ledger, as alert inputs.
 *
 * - a row not seen before → `Recorded`
 * - a row whose signature moved (reviewed, evidence added, incident linked) → silent update
 * - a row that left the active list → `Gone` (the poll cannot tell an agent's withdrawal from
 *   another admin's reinstatement, so it never rings for it — the hub can and does)
 */
export const diffActive = (previous: Map<number, ICancellationRow>, next: ICancellationRow[]): IReceiveInput[] => {
	const out: IReceiveInput[] = [];
	const nextIds = new Set<number>();
	for (const row of next) {
		nextIds.add(row.id);
		const before = previous.get(row.id);
		if (!before) {
			out.push({ id: row.id, version: row.version ?? null, changeKind: 'Recorded', row });
		} else if (rowSignature(before) !== rowSignature(row)) {
			const changeKind =
				before.reviewStatus !== row.reviewStatus
					? 'Reviewed'
					: before.vesrIncidentId !== row.vesrIncidentId
						? 'IncidentLinked'
						: 'EvidenceAdded';
			out.push({ id: row.id, version: row.version ?? null, changeKind, row });
		}
	}
	for (const [id, row] of previous) {
		// No version: the row's last-known version is not newer than the alert's, and the store
		// would drop the change as stale.
		if (!nextIds.has(id)) out.push({ id, version: null, changeKind: 'Gone', row });
	}
	return out;
};

const usePageVisible = () => {
	const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState !== 'hidden');
	useEffect(() => {
		const onChange = () => setVisible(document.visibilityState !== 'hidden');
		document.addEventListener('visibilitychange', onChange);
		return () => document.removeEventListener('visibilitychange', onChange);
	}, []);
	return visible;
};

export interface ICancellationFeed {
	transport: 'live' | 'polling' | 'off';
	hubStatus: HubStatus | null;
}

/**
 * Feeds the shell alert store for one (election, party).
 *
 * `CancellationChanged` over the shared Live Election hub when the server advertises
 * `liveEvents`; otherwise the active ledger is polled (30 s visible, 120 s hidden). Either
 * way the FIRST load seeds the seen-set silently — opening the app on election night must
 * not ring for 300 units that were cancelled before you sat down — and a reconnect refetches,
 * because events were missed while the socket was down.
 */
export function useCancellationFeed(args: {
	scope: ICancellationScope;
	capabilities: ICancellationCapabilities | undefined;
	enabled: boolean;
	onRing: (alert: ICancellationAlert) => void;
}): ICancellationFeed {
	const { scope, capabilities, enabled, onRing } = args;
	const visible = usePageVisible();
	const live = !!capabilities?.liveEvents;
	const active = enabled && !!capabilities;
	const [hubStatus, setHubStatus] = useState<HubStatus | null>(null);
	const queryClient = useQueryClient();
	const onRingRef = useRef(onRing);
	onRingRef.current = onRing;

	useEffect(() => {
		cancellationAlerts.setScope(active ? scope.electionId : 0, active ? scope.partyKey : 0);
	}, [active, scope.electionId, scope.partyKey]);

	// While the hub is live the poll stays off; it still runs once to seed, and on reconnect.
	const pollMs = !active ? false : live && hubStatus === 'live' ? false : visible ? POLL_VISIBLE_MS : POLL_HIDDEN_MS;
	const { data, refetch } = useActiveCancellations({
		electionId: scope.electionId,
		partyId: scope.partyId,
		partyKey: scope.partyKey,
		enabled: active,
		refetchInterval: pollMs,
	});

	const ring = (input: IReceiveInput) => {
		if (!cancellationAlerts.receive({ ...input, own: isOwnChange(input, getUserData().user?.UserId) })) return;
		const alert = cancellationAlerts.getSnapshot().alerts.find((a) => a.id === input.id);
		if (!alert) return;
		const claim = () => {
			if (claimChime(alert.signature)) onRingRef.current(alert);
		};
		// A visible tab claims first; a background tab waits so the visible one wins the race.
		if (typeof document !== 'undefined' && document.visibilityState === 'hidden') setTimeout(claim, 400);
		else claim();
	};
	const ringRef = useRef(ring);
	ringRef.current = ring;

	// Poll diff. Keyed per scope so a party switch re-seeds instead of alerting on every row.
	const known = useRef<{ scope: string; rows: Map<number, ICancellationRow> } | null>(null);
	const rows = data?.rows;
	useEffect(() => {
		if (!rows || !active) return;
		const scopeKey = `${scope.electionId}:${scope.partyKey}`;
		const nextMap = new Map(rows.map((r) => [r.id, r]));
		if (known.current?.scope !== scopeKey) {
			cancellationAlerts.seed(rows);
			known.current = { scope: scopeKey, rows: nextMap };
			return;
		}
		const changes = diffActive(known.current.rows, rows);
		known.current = { scope: scopeKey, rows: nextMap };
		if (changes.length === 0) return;
		invalidateLedgerViews(queryClient, scope.electionId, scope.partyKey);
		for (const input of changes) {
			if (input.changeKind !== 'Gone') {
				ringRef.current(input);
				continue;
			}
			// Withdrawn or reinstated? Only the ledger knows; the poll holds the stale row.
			void resolveGoneRow(scope.electionId, scope.partyId, input.row).then((fresh) => {
				if (known.current?.scope !== scopeKey) return;
				ringRef.current(
					fresh ? { id: fresh.id, version: fresh.version ?? null, changeKind: fresh.state as string, row: fresh } : input
				);
			});
		}
	}, [rows, active, scope.electionId, scope.partyId, scope.partyKey, queryClient]);

	useEffect(() => {
		const lease = active && live ? acquireLiveElectionHub(scope.electionId, scope.partyId) : null;
		if (!lease) {
			setHubStatus(null);
			return undefined;
		}
		lease.on('CancellationChanged', (payload) => {
			const event = payload as ICancellationChangedEvent | undefined;
			if (!event?.row || !event.id) return;
			if (Number(event.row.electionId) && Number(event.row.electionId) !== scope.electionId) return;
			// Not until the seed has run: an event racing the first load is covered by it.
			if (!known.current) return;
			ringRef.current({
				id: event.id,
				version: event.version ?? event.row.version ?? null,
				changeKind: event.changeKind,
				row: event.row,
			});
			// Keep the poll baseline in step so the next poll does not re-diff this change.
			const map = new Map(known.current.rows);
			if (event.row.state === undefined || event.row.state === 'Active') map.set(event.id, event.row);
			else map.delete(event.id);
			known.current = { ...known.current, rows: map };
			invalidateLedgerViews(queryClient, scope.electionId, scope.partyKey);
		});
		lease.onStatus(() => setHubStatus(lease.getStatus()));
		lease.onReconnected(() => void refetch());
		setHubStatus(lease.getStatus());
		return () => lease.release();
	}, [active, live, scope.electionId, scope.partyId, scope.partyKey, refetch, queryClient]);

	return {
		transport: !active ? 'off' : live && hubStatus === 'live' ? 'live' : 'polling',
		hubStatus,
	};
}
